import type { SupabaseClient } from "@supabase/supabase-js";

export type SubscriberLifecycleStatus =
  | "pending_entry"
  | "active"
  | "suspended"
  | "expired"
  | "cancelled";

export interface SubscriberManagementRow {
  provisionId: string;
  farmId: string;
  farmName: string;
  managerName: string;
  subscriptionDays: number;
  setupCodeLast4: string;
  claimedUserId?: string;
  claimedEmail?: string;
  createdAt: string;
  claimedAt?: string;
  subscriptionId?: string;
  subscriptionCode?: string;
  status: SubscriberLifecycleStatus;
  startsOn?: string;
  expiresOn?: string;
  daysRemaining?: number;
}

export interface SubscriberProvisionResult {
  provisionId: string;
  farmId: string;
  farmName?: string;
  managerName?: string;
  subscriptionDays?: number;
  status: string;
  setupCode: string;
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function rows(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map(record) : [];
}

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function optionalNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return undefined;
}

function lifecycleStatus(value: unknown): SubscriberLifecycleStatus {
  if (
    value === "pending_entry" ||
    value === "active" ||
    value === "suspended" ||
    value === "expired" ||
    value === "cancelled"
  ) {
    return value;
  }
  throw new Error("SUBSCRIBER_STATUS_INVALID");
}

export async function loadPlatformSubscribers(
  client: SupabaseClient,
): Promise<SubscriberManagementRow[]> {
  const { data, error } = await client.rpc("platform_subscriber_dashboard_v1");
  if (error) throw error;

  return rows(data).map((row) => {
    const claimedUserId = optionalString(row.claimed_user_id);
    const claimedEmail = optionalString(row.claimed_email);
    const claimedAt = optionalString(row.claimed_at);
    const subscriptionId = optionalString(row.subscription_id);
    const subscriptionCode = optionalString(row.subscription_code);
    const startsOn = optionalString(row.starts_on);
    const expiresOn = optionalString(row.expires_on);
    const daysRemaining = optionalNumber(row.days_remaining);

    return {
      provisionId: String(row.provision_id),
      farmId: String(row.farm_id),
      farmName: String(row.farm_name ?? "سالن"),
      managerName: String(row.manager_name ?? "مدیر"),
      subscriptionDays: Number(row.subscription_days ?? 0),
      setupCodeLast4: String(row.setup_code_last4 ?? ""),
      createdAt: String(row.created_at ?? ""),
      status: lifecycleStatus(row.status),
      ...(claimedUserId ? { claimedUserId } : {}),
      ...(claimedEmail ? { claimedEmail } : {}),
      ...(claimedAt ? { claimedAt } : {}),
      ...(subscriptionId ? { subscriptionId } : {}),
      ...(subscriptionCode ? { subscriptionCode } : {}),
      ...(startsOn ? { startsOn } : {}),
      ...(expiresOn ? { expiresOn } : {}),
      ...(daysRemaining === undefined ? {} : { daysRemaining }),
    };
  });
}

function provisionResult(value: unknown): SubscriberProvisionResult {
  const row = record(value);
  const setupCode = optionalString(row.setupCode);
  const provisionId = optionalString(row.provisionId);
  const farmId = optionalString(row.farmId);

  if (!setupCode || !provisionId || !farmId) {
    throw new Error("SUBSCRIBER_PROVISION_RESULT_INVALID");
  }

  const farmName = optionalString(row.farmName);
  const managerName = optionalString(row.managerName);
  const subscriptionDays = optionalNumber(row.subscriptionDays);

  return {
    provisionId,
    farmId,
    status: String(row.status ?? "pending_entry"),
    setupCode,
    ...(farmName ? { farmName } : {}),
    ...(managerName ? { managerName } : {}),
    ...(subscriptionDays === undefined ? {} : { subscriptionDays }),
  };
}

export async function createPlatformSubscriber(
  client: SupabaseClient,
  managerName: string,
  farmName: string,
  days: number,
): Promise<SubscriberProvisionResult> {
  const { data, error } = await client.rpc("platform_subscriber_create_v1", {
    target_manager_name: managerName,
    target_farm_name: farmName,
    target_days: days,
  });
  if (error) throw error;
  return provisionResult(data);
}

export async function reissuePlatformSubscriberCode(
  client: SupabaseClient,
  provisionId: string,
): Promise<SubscriberProvisionResult> {
  const { data, error } = await client.rpc("platform_subscriber_reissue_code_v1", {
    target_provision_id: provisionId,
  });
  if (error) throw error;
  return provisionResult(data);
}

export async function claimPlatformSubscriberSetup(
  client: SupabaseClient,
  setupCode: string,
): Promise<string> {
  const { data, error } = await client.rpc("platform_subscriber_claim_v1", {
    target_setup_code: setupCode,
  });
  if (error) throw error;
  const row = record(data);
  const farmId = optionalString(row.farmId);
  if (!farmId) throw new Error("SUBSCRIBER_CLAIM_RESULT_INVALID");
  return farmId;
}
